import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ArrowUpRight, Check, X, Loader2, MessageCircle, CalendarRange, Users, BedDouble,
  UtensilsCrossed, Navigation, Car, Mountain, Clock3, Bell, GraduationCap, Lock, ShieldCheck,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AeroNav } from "@/components/aero/AeroNav";
import { AeroFooter } from "@/components/aero/AeroFooter";
import { WhatsAppFloat } from "@/components/cine/WhatsAppFloat";
import { MountainVideo } from "@/components/aero/MountainVideo";
import { useAuth } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const loadRazorpay = () =>
  new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });

const HERO_IMG = "/assets/fs-teaser.jpg";

const HERO_STATS = ["3 Days", "10 Seats", "Stay Included", "Meals Included"];

const COMPARE = {
  typical: ["Classroom-heavy", "Limited flying", "Generic lessons", "Little real-world practice"],
  ours: ["Lucknow Location Shoot", "Hands-on FPV flying", "Small batch mentoring", "Cinematic production workflow"],
};

const JOURNEY = [
  { day: "Day 1", title: "Foundations & First Flight", items: ["FPV Basics", "DJI Goggles", "First Flight"] },
  { day: "Day 2", title: "Cinematic Movement", items: ["Cinematic Movement", "Tracking Shots", "Real Outdoor Flying"] },
  { day: "Day 3", title: "Live Shoot & Delivery", items: ["Live Shoot", "Editing Workflow", "Final Project"] },
];

const INCLUDED = [
  { icon: Navigation, title: "DJI Avata 2 Training", sub: "Hands-on with pro gear" },
  { icon: BedDouble, title: "Hotel Stay", sub: "2 nights included" },
  { icon: UtensilsCrossed, title: "Meals", sub: "All meals covered" },
  { icon: Car, title: "Local Transport", sub: "Locations to hotel" },
];

const GALLERY = [
  { img: "/assets/bts-1.jpg", caption: "On set — Your Honor S2" },
  { img: "/assets/bts-9.jpg", caption: "Pre-flight walkthrough" },
  { img: "/assets/hero-main.jpg", caption: "Mountain operations" },
  { img: "/assets/bts-6.jpg", caption: "Camera unit — film set" },
  { img: "/assets/bts-4.jpg", caption: "Varanasi Ghats unit" },
  { img: "/assets/bts-2.jpg", caption: "With Anubhav Sinha" },
];

const FAQS = [
  { q: "Is it beginner friendly?", a: "Haan — bilkul. Day 1 zero se start hota hai: FPV basics, goggles orientation, aur pehli flight. Kisibhi experience level ke liye designed hai." },
  { q: "Do I need my own drone?", a: "Nahi. Saari training DJI Avata 2 par hoti hai jo main provide karta hoon. Apna drone hai toh la sakte ho — usi par settings set kar dunga." },
  { q: "Where will it happen?", a: "Real mountain locations (Himachal / Uttarakhand belt). Exact location batch ke saath share hoti hai — kyunki real flying real locations par hi seekhne ko milti hai." },
  { q: "What should I bring?", a: "Bas comfortable outdoor clothes, ek notebook, aur enthusiasm. Saara gear, goggles, aur safety equipment provided hai." },
  { q: "Is hotel included?", a: "Haan — 2 nights ka stay, saare meals, aur local transport included hai. Aap sirf pahuncho, baaki sab hum sambhal lenge." },
  { q: "How do I reserve my seat?", a: "Neeche 'Reserve Your Seat' par click karo — naam aur contact chhodo, main personally call karke seat confirm karta hoon. Sirf 10 seats hain." },
];

const btnPrimary =
  "inline-flex items-center justify-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full shadow-[0_15px_35px_-12px_rgba(15,42,67,0.5)] hover:bg-[#16395C] hover:-translate-y-0.5 transition-all duration-300";
const btnGhost =
  "inline-flex items-center justify-center gap-2 border border-white/35 text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300";
const btnOutlineNavy =
  "inline-flex items-center justify-center gap-2 bg-white border border-[#0F2A43]/25 text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#EEF2F6] hover:border-[#0F2A43] transition-all duration-300";

const Head = ({ label, title, sub, center = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease }}
    className={center ? "max-w-2xl mx-auto text-center" : "max-w-2xl"}
  >
    <p className={`font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A] ${center ? "flex items-center justify-center gap-4" : ""}`}>
      {label}
    </p>
    <h2 className="mt-4 font-serif text-[34px] md:text-[48px] leading-[1.05] text-[#111827]">{title}</h2>
    <motion.span
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: 0.25, ease }}
      className={`mt-5 block w-14 h-[2px] bg-[#4F6D8A] ${center ? "mx-auto" : "origin-left"}`}
    />
    {sub && <p className="mt-5 text-[#6B7280] leading-relaxed">{sub}</p>}
  </motion.div>
);

const Perforation = () => (
  <div
    aria-hidden
    className="h-5 bg-[#0F2A43]"
    style={{ backgroundImage: "radial-gradient(circle, #F8F6F2 2.5px, transparent 3px)", backgroundSize: "26px 100%", backgroundPosition: "center" }}
  />
);

export default function FilmSchoolPage() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [waHref, setWaHref] = useState("https://wa.me/?text=Hi%20Akhilesh");
  const [seat, setSeat] = useState({ name: "", phone: "" });
  const [seatsLeft, setSeatsLeft] = useState(null);
  const [courses, setCourses] = useState([]);
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  // course enrollment (Razorpay)
  const [buyCourse, setBuyCourse] = useState(null);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
  const [paying, setPaying] = useState(false);
  const [enrollDone, setEnrollDone] = useState(null);
  const [sendingSeat, setSendingSeat] = useState(false);
  const [seatDone, setSeatDone] = useState(false);
  const glowRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 700], [0, 70]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/config`)
      .then((r) => {
        if (r.data.whatsapp_number) {
          setWaHref(
            `https://wa.me/${r.data.whatsapp_number}?text=${encodeURIComponent("Hi Akhilesh, I want to reserve my seat for the DJI Avata 2 FPV Masterclass (October batch).")}`
          );
        }
      })
      .catch(() => {});
    axios
      .get(`${API}/workshop/seats`)
      .then((r) => setSeatsLeft(r.data?.oct_2026?.remaining ?? null))
      .catch(() => {});
    axios
      .get(`${API}/courses`)
      .then((r) => setCourses(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setCoursesLoaded(true));
  }, []);

  const goCheckout = () => navigate("/book-workshop");

  const onMouseMove = (e) => {
    if (glowRef.current) {
      glowRef.current.style.background = `radial-gradient(650px at ${e.clientX}px ${e.clientY + window.scrollY}px, rgba(79,109,138,0.09), transparent 70%)`;
    }
  };

  const reserveSeat = async () => {
    if (seat.name.trim().length < 2 || seat.phone.trim().length < 10) {
      toast.error("Naam aur valid phone number daalo");
      return;
    }
    setSendingSeat(true);
    try {
      await axios.post(`${API}/leads`, {
        name: seat.name.trim(),
        email: `${seat.phone.trim().replace(/\D/g, "")}@seat.filmmakerakki`,
        resource: "fpv-masterclass-seat-oct26",
      });
      setSeatDone(true);
      toast.success("Seat request received! Main personally call karunga.");
    } catch {
      toast.error("Kuch gadbad ho gayi — WhatsApp par message kar do.");
    } finally {
      setSendingSeat(false);
    }
  };

  const scrollToBooking = () => document.querySelector("#fs-booking")?.scrollIntoView({ behavior: "smooth", block: "center" });

  // flagship workshop course — fully admin-managed (Courses Manager), static values = fallback
  const flagship = courses.find((c) => c.tracking === "workshop") || null;
  const flagshipName = flagship?.name || "DJI Avata 2 FPV Masterclass";
  const flagshipDesc = flagship?.description || "3 days. Real mountain locations. Real FPV flying. Real cinematic output — ek complete immersive filmmaking experience.";
  const flagshipPrice = flagship ? flagship.price : 12000;
  const flagshipStatus = flagship?.status || "active";
  const gridCourses = courses.filter((c) => c.tracking !== "workshop");

  const openEnroll = (c) => {
    if (!user) {
      toast.error("Enrollment ke liye pehle Google se sign in karo");
      login();
      return;
    }
    setBuyer({ name: user.name || "", email: user.email || "", phone: "" });
    setEnrollDone(null);
    setBuyCourse(c);
  };

  const enrollPay = async () => {
    if (buyer.name.trim().length < 2 || !/^\S+@\S+\.\S+$/.test(buyer.email)) {
      toast.error("Naam aur valid email daalo — course details isi par jayenge");
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/course-orders/create-order`, {
        course_id: buyCourse.id,
        name: buyer.name.trim(),
        email: buyer.email.trim(),
        phone: buyer.phone.trim(),
      });
      if (data.free) {
        setEnrollDone(data.purchase_id);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("rzp load failed");
      const rzp = new window.Razorpay({
        key: data.key_id,
        order_id: data.order_id,
        amount: data.amount,
        currency: "INR",
        name: "FILMMAKERAKKI",
        description: buyCourse.name,
        prefill: { name: buyer.name, email: buyer.email, contact: buyer.phone },
        theme: { color: "#0F2A43" },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/course-orders/verify`, {
              booking_id: data.purchase_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setEnrollDone(data.purchase_id);
          } catch {
            toast.error("Payment verify nahi hua — screenextfilms@gmail.com par contact karo.");
          }
        },
        modal: { ondismiss: () => toast.error("Payment cancel hua — enrollment complete nahi hui.") },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment start nahi ho paya — dobara try karo.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      onMouseMove={onMouseMove}
      className="relative bg-[#F8F6F2] text-[#111827] overflow-x-clip"
      data-testid="film-school-page"
    >
      {/* mouse-follow cinematic glow (desktop) */}
      <div ref={glowRef} aria-hidden className="pointer-events-none absolute inset-0 z-0 hidden lg:block" />

      <AeroNav />

      {/* 1 — Hero Experience */}
      <section ref={heroRef} className="relative overflow-hidden bg-gradient-to-br from-[#0F2A43] via-[#12305A] to-[#0B1F33]" data-testid="fs-hero">
        {/* mobile image */}
        <div className="lg:hidden relative h-[330px]">
          <img src={HERO_IMG} alt="Akhilesh Kumar Suman — FPV studio portrait with DJI Avata 2" className="absolute inset-0 w-full h-full object-cover object-[center_30%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A43] via-transparent to-transparent" />
        </div>
        {/* desktop image — parallax, edge-to-edge right */}
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[56%] overflow-hidden">
          <motion.img
            src={HERO_IMG}
            alt="Akhilesh Kumar Suman — FPV studio portrait with DJI Avata 2"
            style={{ y: heroImgY, WebkitMaskImage: "linear-gradient(to right, transparent, black 24%)", maskImage: "linear-gradient(to right, transparent, black 24%)" }}
            className="absolute inset-0 w-full h-[112%] object-cover object-[center_30%]"
          />
        </div>
        <div className="relative z-10 max-w-[1320px] mx-auto px-6 md:px-10">
          <div className="lg:max-w-[46%] py-14 lg:py-32">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease }}>
              <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#8FAECC] flex items-center gap-4">
                <span className="w-10 h-px bg-[#4F6D8A] inline-block" /> Film School
              </p>
              <h1 className="mt-5 font-serif text-[44px] md:text-[64px] xl:text-[72px] leading-[1.02] text-white">
                Fly Like a Filmmaker
              </h1>
              <p className="mt-3 font-display font-bold text-lg md:text-xl text-[#8FAECC]">{flagshipName}</p>
              <p className="mt-5 text-white/70 leading-relaxed max-w-lg">
                Learn cinematic FPV flying through a real 3-day immersive experience designed by a Bollywood aerial cinematographer.
              </p>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                {HERO_STATS.map((s) => (
                  <p key={s} className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/85 flex items-center gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8FAECC]" /> {s}
                  </p>
                ))}
              </div>
              <div className="mt-9 flex flex-wrap gap-4">
                <button data-testid="fs-hero-reserve" onClick={goCheckout} className="inline-flex items-center justify-center gap-2 bg-white text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#8FAECC] hover:text-white hover:-translate-y-0.5 transition-all duration-300">
                  Reserve Your Seat <ArrowRight size={13} />
                </button>
                <button data-testid="fs-hero-experience" onClick={scrollToBooking} className={btnGhost}>
                  See the Experience
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2 — Why this is different */}
      <section className="py-[90px] md:py-[110px]" data-testid="fs-why">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <Head label="The Difference" title="Why This Isn't a Normal Workshop" center />
          <div className="mt-14 grid md:grid-cols-2 gap-7 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, ease }}
              className="bg-white rounded-[22px] border border-[#E5E7EB] p-8 md:p-10"
              data-testid="fs-compare-typical"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">Typical Drone Course</p>
              <ul className="mt-7 space-y-4">
                {COMPARE.typical.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-[#6B7280]">
                    <span className="w-6 h-6 rounded-full bg-[#F8F6F2] border border-[#E5E7EB] flex items-center justify-center shrink-0">
                      <X size={11} strokeWidth={2.5} className="text-[#9CA3AF]" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: 0.12, ease }}
              className="bg-[#0F2A43] rounded-[22px] p-8 md:p-10 shadow-[0_35px_80px_-25px_rgba(15,42,67,0.55)] md:-translate-y-3"
              data-testid="fs-compare-ours"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#8FAECC]">FILMMAKERAKKI Experience</p>
              <ul className="mt-7 space-y-4">
                {COMPARE.ours.map((t) => (
                  <li key={t} className="flex items-center gap-3 text-white/90">
                    <span className="w-6 h-6 rounded-full bg-white/10 border border-white/20 flex items-center justify-center shrink-0">
                      <Check size={11} strokeWidth={2.5} className="text-[#8FAECC]" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3 — Flagship Experience */}
      <section className="py-[90px] md:py-[110px] bg-white border-y border-[#E5E7EB]" data-testid="fs-flagship">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease }}
            className="relative bg-[#F8F6F2] rounded-[28px] border border-[#E5E7EB] p-8 md:p-14 shadow-[0_35px_80px_-30px_rgba(15,42,67,0.3)]"
          >
            <motion.span
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 right-8 bg-[#0F2A43] text-white font-mono text-[9px] font-bold tracking-[0.25em] uppercase px-5 py-2.5 rounded-full shadow-lg"
              data-testid="fs-limited-badge"
            >
              Limited Seats
            </motion.span>
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">Flagship Experience</p>
                <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#111827] leading-tight" data-testid="fs-flagship-name">{flagshipName}</h2>
                <p className="mt-5 text-[#6B7280] leading-relaxed" data-testid="fs-flagship-desc">
                  {flagshipDesc}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  {["3 Days", "10 Creators", "Hotel + Meals", "Real Locations"].map((t) => (
                    <span key={t} className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#0F2A43] bg-white border border-[#E5E7EB] px-4 py-2 rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="md:text-right">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">Price</p>
                <p className="mt-2 font-display font-black text-5xl md:text-6xl text-[#0F2A43]" data-testid="fs-flagship-price">₹{flagshipPrice.toLocaleString("en-IN")}</p>
                <p className="mt-1 font-mono text-[9px] tracking-[0.25em] uppercase text-[#6B7280]">Per Participant</p>
                <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-[#6B7280]">Batch</p>
                <p className="mt-1 font-display font-bold text-xl text-[#111827]">Only 10 Creators</p>
                {flagshipStatus === "active" ? (
                  <button data-testid="fs-flagship-reserve" onClick={goCheckout} className={`${btnPrimary} mt-8 w-full md:w-auto`}>
                    Reserve Your Seat <ArrowRight size={13} />
                  </button>
                ) : flagshipStatus === "coming_soon" ? (
                  <button data-testid="fs-flagship-reserve" onClick={scrollToBooking} className={`${btnOutlineNavy} mt-8 w-full md:w-auto`}>
                    <Bell size={13} /> Notify Me
                  </button>
                ) : (
                  <span data-testid="fs-flagship-reserve" className="mt-8 inline-flex items-center gap-2 bg-[#F3F4F6] text-[#9CA3AF] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full cursor-not-allowed">
                    Enrollment Closed
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 3.5 — Online Courses (admin-managed storefront) */}
      {(gridCourses.length > 0 || !coursesLoaded) && (
        <section className="py-[90px] md:py-[110px]" data-testid="fs-courses-section">
          <div className="max-w-[1320px] mx-auto px-6 md:px-10">
            <Head label="Online Courses" title="Learn At Your Own Pace" sub="Recorded aur online programs — ghar baithe, apne time par. Naye courses yahin launch hote hain." />
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {!coursesLoaded &&
                [0, 1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-[22px] border border-[#E5E7EB] overflow-hidden animate-pulse" data-testid={`fs-course-skeleton-${i}`}>
                    <div className="aspect-video bg-[#E5E7EB]/70" />
                    <div className="p-7 space-y-3">
                      <div className="h-5 w-3/4 rounded-lg bg-[#E5E7EB]" />
                      <div className="h-3.5 w-full rounded-lg bg-[#EEF0F3]" />
                      <div className="h-3.5 w-2/3 rounded-lg bg-[#EEF0F3]" />
                      <div className="pt-5 mt-2 border-t border-[#E5E7EB] flex items-center justify-between">
                        <div className="h-6 w-16 rounded-lg bg-[#E5E7EB]" />
                        <div className="h-9 w-28 rounded-full bg-[#E5E7EB]" />
                      </div>
                    </div>
                  </div>
                ))}
              {gridCourses.map((c, i) => {
                const isActive = c.status === "active";
                const isClosed = c.status === "closed";
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: i * 0.1, ease }}
                    data-testid={`fs-course-card-${c.id}`}
                    className="group bg-white rounded-[22px] border border-[#E5E7EB] overflow-hidden shadow-[0_15px_45px_-18px_rgba(15,42,67,0.2)] hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-20px_rgba(15,42,67,0.35)] transition-all duration-300 flex flex-col"
                  >
                    <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[#0F2A43] via-[#12305A] to-[#0B1F33]">
                      {c.thumbnail_url ? (
                        <img src={c.thumbnail_url} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <span className="absolute inset-0 flex items-center justify-center">
                          <GraduationCap size={44} strokeWidth={1.2} className="text-white/25" />
                        </span>
                      )}
                      <span
                        data-testid={`fs-course-status-${c.id}`}
                        className={`absolute top-4 left-4 font-mono text-[8px] font-bold tracking-[0.22em] uppercase px-3.5 py-1.5 rounded-full ${
                          isActive ? "bg-emerald-500 text-white" : isClosed ? "bg-[#6B7280] text-white" : "bg-[#0F2A43] text-white"
                        }`}
                      >
                        {isActive ? "Enrollment Open" : isClosed ? "Closed" : "Coming Soon"}
                      </span>
                      {c.course_type && (
                        <span className="absolute top-4 right-4 font-mono text-[8px] font-bold tracking-[0.22em] uppercase bg-white/90 backdrop-blur text-[#0F2A43] px-3.5 py-1.5 rounded-full" data-testid={`fs-course-type-${c.id}`}>
                          {c.course_type}
                        </span>
                      )}
                    </div>
                    <div className="p-7 flex flex-col flex-1">
                      <h3 className="font-display font-bold text-xl text-[#111827] leading-snug">{c.name}</h3>
                      {c.description && <p className="mt-2.5 text-sm text-[#6B7280] leading-relaxed line-clamp-3">{c.description}</p>}
                      <div className="flex-1 min-h-[20px]" />
                      <div className="pt-5 border-t border-[#E5E7EB] flex items-center justify-between gap-4">
                        <div>
                          <p className="font-display font-black text-2xl text-[#0F2A43]" data-testid={`fs-course-price-${c.id}`}>
                            {c.price > 0 ? `₹${Number(c.price).toLocaleString("en-IN")}` : "Free"}
                          </p>
                          {c.launch_date && (
                            <p className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-[#6B7280]">
                              {isActive ? "Started" : "Launching"} {new Date(c.launch_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            </p>
                          )}
                        </div>
                        {isActive ? (
                          c.enrollment_link ? (
                            <a
                              data-testid={`fs-course-cta-${c.id}`}
                              href={c.enrollment_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-5 py-3 rounded-full hover:bg-[#16395C] transition-colors"
                            >
                              Enroll Now <ArrowRight size={11} />
                            </a>
                          ) : (
                            <button
                              data-testid={`fs-course-cta-${c.id}`}
                              onClick={() => openEnroll(c)}
                              className="inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-5 py-3 rounded-full hover:bg-[#16395C] transition-colors"
                            >
                              <Lock size={10} /> Enroll Now
                            </button>
                          )
                        ) : (
                          <span
                            data-testid={`fs-course-cta-${c.id}`}
                            className="inline-flex items-center gap-2 bg-[#F8F6F2] border border-[#E5E7EB] text-[#9CA3AF] font-mono text-[9px] font-bold tracking-[0.2em] uppercase px-5 py-3 rounded-full cursor-not-allowed"
                          >
                            {isClosed ? "Enrollment Closed" : "Coming Soon"}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 4 — 3-Day Journey */}
      <section className="py-[90px] md:py-[110px]" data-testid="fs-journey">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <Head label="The Journey" title="Your 3-Day Journey" sub="Har din ek milestone — pehli flight se final cinematic project tak." />
          <div className="mt-14 relative">
            <span className="hidden md:block absolute top-9 left-0 right-0 h-px bg-[#4F6D8A]/30" aria-hidden />
            <div className="grid md:grid-cols-3 gap-7 relative">
              {JOURNEY.map((d, i) => (
                <motion.div
                  key={d.day}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.12, ease }}
                  data-testid={`fs-journey-${i}`}
                  className="bg-white rounded-[22px] border border-[#E5E7EB] p-8 shadow-[0_15px_45px_-18px_rgba(15,42,67,0.2)] hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-20px_rgba(15,42,67,0.35)] transition-all duration-300"
                >
                  <span className="inline-flex w-[72px] h-[72px] rounded-full bg-[#0F2A43] text-white font-serif text-xl items-center justify-center relative z-10">
                    {d.day.split(" ")[1]}
                  </span>
                  <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A]">{d.day}</p>
                  <p className="mt-2 font-display font-bold text-xl text-[#111827]">{d.title}</p>
                  <ul className="mt-5 space-y-2.5">
                    {d.items.map((t) => (
                      <li key={t} className="flex items-center gap-2.5 text-sm text-[#6B7280]">
                        <Check size={13} strokeWidth={2} className="text-[#4F6D8A] shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5 — Everything Included */}
      <section className="py-[90px] md:py-[110px] bg-white border-y border-[#E5E7EB]" data-testid="fs-included">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <Head label="What's Included" title="Everything Included" sub="Sirf pahunchna hai — baaki sab covered." />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {INCLUDED.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.5, delay: i * 0.07, ease }}
                  data-testid={`fs-included-${i}`}
                  className="bg-white/70 backdrop-blur rounded-[22px] border border-[#E5E7EB] p-7 text-center shadow-[0_15px_40px_-18px_rgba(15,42,67,0.2)] hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-20px_rgba(15,42,67,0.35)] transition-all duration-300"
                >
                  <span className="inline-flex w-12 h-12 rounded-full bg-[#4F6D8A]/10 items-center justify-center">
                    <Icon size={20} strokeWidth={1.4} className="text-[#0F2A43]" />
                  </span>
                  <p className="mt-4 font-display font-bold text-base text-[#111827]">{t.title}</p>
                  <p className="mt-1.5 font-mono text-[8px] tracking-[0.2em] uppercase text-[#6B7280]">{t.sub}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 6 — Instructor */}
      <section className="py-[90px] md:py-[110px]" data-testid="fs-instructor">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-[#4F6D8A]/50 rounded-tl-2xl" />
            <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-2 border-r-2 border-[#4F6D8A]/50 rounded-br-2xl" />
            <img
              src="/assets/about-main.jpg"
              alt="Akhilesh Kumar Suman — aerial cinematographer on set"
              loading="lazy"
              className="w-full aspect-[4/5] object-cover object-center rounded-[22px] shadow-[0_30px_80px_-30px_rgba(15,42,67,0.45)]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease }}
            className="lg:col-span-7"
          >
            <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#4F6D8A]">Your Instructor</p>
            <h2 className="mt-4 font-serif text-[34px] md:text-[48px] leading-[1.05] text-[#111827]">
              Learn From Someone Who Actually Shoots Films
            </h2>
            <span className="mt-5 block w-14 h-[2px] bg-[#4F6D8A]" />
            <p className="mt-6 text-[#374151] text-lg leading-relaxed max-w-xl">
              Learn from real production experience — not recycled YouTube tutorials.
            </p>
            <p className="mt-4 text-[#6B7280] leading-relaxed max-w-xl">
              9+ years, 26+ productions — feature films, OTT series, aur government documentaries ke real sets par jo seekha hai, wahi 3 din mein hands-on sikhaunga. Gadar 2 se lekar Your Honor tak — wahi workflows, wahi discipline.
            </p>
            <p className="mt-7 font-[Caveat] text-3xl text-[#111827]/70 -rotate-2 select-none">Akhilesh Kumar Suman</p>
          </motion.div>
        </div>
      </section>

      {/* 7 — Experience Gallery (film strip) */}
      <section className="py-[90px] md:py-[110px]" data-testid="fs-gallery">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          <Head label="Experience Gallery" title="Moments From the Field" />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease }}
            className="mt-12 rounded-[22px] overflow-hidden shadow-[0_25px_60px_-20px_rgba(15,42,67,0.35)]"
          >
            <Perforation />
            <div className="bg-[#0F2A43] py-5">
              <div className="flex gap-5 overflow-x-auto px-5 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {GALLERY.map((g, i) => (
                  <figure key={g.caption} className="group shrink-0 w-[230px] md:w-[280px] snap-start" data-testid={`fs-gallery-${i}`}>
                    <div className="rounded-[14px] overflow-hidden border border-white/10">
                      <img src={g.img} alt={g.caption} loading="lazy" className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    <figcaption className="mt-3 font-mono text-[8px] tracking-[0.25em] uppercase text-white/60 group-hover:text-white transition-colors">
                      {String(i + 1).padStart(2, "0")} — {g.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
            <Perforation />
          </motion.div>
        </div>
      </section>

      {/* 8 — Drone Workshop (Coming Soon) */}
      <section className="py-[90px] md:py-[110px] bg-white border-t border-[#E5E7EB]" data-testid="fs-workshop-teaser">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease }}
            className="text-center bg-[#F8F6F2] border border-[#E5E7EB] rounded-[28px] p-10 md:p-16 shadow-[0_25px_70px_-25px_rgba(15,42,67,0.25)]"
          >
            <span className="inline-flex items-center gap-1.5 bg-[#0F2A43] text-white font-mono text-[8px] font-bold tracking-[0.25em] uppercase px-4 py-2 rounded-full">
              <Clock3 size={10} /> Coming Soon
            </span>
            <h2 className="mt-6 font-serif text-3xl md:text-5xl text-[#111827] leading-tight">Drone Cinematography Workshop</h2>
            <p className="mt-5 text-[#6B7280] leading-relaxed max-w-xl mx-auto">
              Learn cinematic aerial filmmaking with professional drone workflows, composition, camera settings, and real-world shooting techniques.
            </p>
            <button
              data-testid="fs-workshop-notify"
              onClick={scrollToBooking}
              className={`${btnOutlineNavy} mt-8`}
            >
              <Bell size={13} /> Notify Me
            </button>
          </motion.div>
        </div>
      </section>

      {/* 10 — FAQ */}
      <section className="py-[90px] md:py-[110px]" data-testid="fs-faq-section">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <Head label="FAQ" title="Questions, Answered" />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease }}
            className="mt-10"
            data-testid="fs-faq"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  data-testid={`fs-faq-${i}`}
                  className="bg-white border border-[#E5E7EB] rounded-[18px] px-6 md:px-8 overflow-hidden hover:border-[#4F6D8A]/50 transition-colors duration-300"
                >
                  <AccordionTrigger className="font-display font-bold text-base md:text-lg text-[#111827] hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#6B7280] leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* 11 — Seat Availability + Final Booking CTA */}
      <section id="fs-booking" className="pb-[100px] md:pb-[120px]" data-testid="fs-booking">
        <div className="max-w-[1320px] mx-auto px-6 md:px-10">
          {/* Seat availability card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, ease }}
            className="max-w-3xl mx-auto bg-white border border-[#E5E7EB] rounded-[24px] p-8 md:p-10 shadow-[0_30px_70px_-25px_rgba(15,42,67,0.35)] relative z-10"
            data-testid="fs-seat-card"
          >
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6D8A] flex items-center gap-2">
                  <CalendarRange size={13} /> Next Batch — October 2026
                </p>
                <p className="mt-3 font-display font-bold text-2xl text-[#111827]" data-testid="fs-seat-count">
                  10 Seats Only · <span className="text-[#0F2A43]">{seatsLeft === null ? "Limited" : `${seatsLeft} Seats`} Available</span>
                </p>
                <p className="mt-1.5 text-sm text-[#6B7280]">Reserve before it fills.</p>
              </div>
              {seatDone ? (
                <p className="inline-flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 font-mono text-[10px] font-bold tracking-[0.2em] uppercase px-6 py-4 rounded-full" data-testid="fs-seat-success">
                  <Check size={14} /> Seat Request Received
                </p>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <input
                    data-testid="fs-seat-name"
                    value={seat.name}
                    onChange={(e) => setSeat((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Name"
                    className="bg-[#F8F6F2] border border-[#E5E7EB] focus:border-[#4F6D8A] outline-none rounded-full px-5 py-3.5 text-sm text-[#111827] placeholder:text-[#9CA3AF] transition-colors sm:w-40"
                  />
                  <input
                    data-testid="fs-seat-phone"
                    value={seat.phone}
                    onChange={(e) => setSeat((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="Phone"
                    className="bg-[#F8F6F2] border border-[#E5E7EB] focus:border-[#4F6D8A] outline-none rounded-full px-5 py-3.5 text-sm text-[#111827] placeholder:text-[#9CA3AF] transition-colors sm:w-44"
                  />
                  <button data-testid="fs-seat-reserve" onClick={reserveSeat} disabled={sendingSeat} className={`${btnPrimary} !px-6 !py-3.5`}>
                    {sendingSeat ? <Loader2 size={14} className="animate-spin" /> : <ArrowUpRight size={13} />} Reserve
                  </button>
                </div>
              )}
            </div>
          </motion.div>

          {/* Final banner */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease }}
            className="relative rounded-[28px] overflow-hidden mt-12 shadow-[0_40px_90px_-25px_rgba(15,42,67,0.55)]"
            data-testid="fs-final-banner"
          >
            <MountainVideo testid="fs-final-mountain-video" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F2A43]/95 via-[#0F2A43]/80 to-[#0F2A43]/30" />
            <div className="relative z-10 px-8 py-16 md:p-20 max-w-2xl">
              <p className="font-mono text-[12px] tracking-[0.35em] uppercase text-[#8FAECC] flex items-center gap-3">
                <Mountain size={14} /> Final Call
              </p>
              <h2 className="mt-5 font-serif text-4xl md:text-[52px] leading-[1.05] text-white">
                Only 10 Seats. One Real Experience.
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed max-w-lg">
                This isn't another online course. It's an immersive filmmaking experience designed for creators who want to fly with confidence.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <button data-testid="fs-final-reserve" onClick={goCheckout} className="inline-flex items-center justify-center gap-2 bg-white text-[#0F2A43] font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#8FAECC] hover:text-white hover:-translate-y-0.5 transition-all duration-300">
                  Reserve Your Seat <ArrowRight size={13} />
                </button>
                <a data-testid="fs-final-whatsapp" href={waHref} target="_blank" rel="noopener noreferrer" className={btnGhost}>
                  <MessageCircle size={13} /> Chat on WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Course enrollment modal (Razorpay) */}
      <AnimatePresence>
        {buyCourse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-[#0F2A43]/60 backdrop-blur-sm flex items-center justify-center px-5"
            onClick={() => !paying && setBuyCourse(null)}
            data-testid="enroll-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.35, ease }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-white rounded-[26px] p-8 md:p-10 shadow-[0_50px_100px_-20px_rgba(15,42,67,0.5)]"
            >
              <button
                onClick={() => !paying && setBuyCourse(null)}
                aria-label="Close"
                data-testid="enroll-close"
                className="absolute top-5 right-5 w-9 h-9 rounded-full border border-[#E5E7EB] text-[#6B7280] hover:text-[#111827] hover:border-[#9CA3AF] flex items-center justify-center transition-colors"
              >
                <X size={15} />
              </button>
              {enrollDone ? (
                <div className="text-center py-4" data-testid="enroll-success">
                  <span className="mx-auto w-16 h-16 rounded-full bg-[#0F2A43] flex items-center justify-center">
                    <Check size={28} strokeWidth={2.5} className="text-white" />
                  </span>
                  <h3 className="mt-6 font-serif text-3xl text-[#111827]">Enrollment Confirmed!</h3>
                  <p className="mt-3 text-sm text-[#6B7280] leading-relaxed">
                    <strong className="text-[#111827]">{buyCourse.name}</strong> ki enrollment confirm ho gayi. Confirmation email <strong className="text-[#111827]">{buyer.email}</strong> par bheji gayi hai — course details isi par aayenge.
                  </p>
                  <p className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-[#4F6D8A]">Enrollment ID: {enrollDone}</p>
                  <button
                    data-testid="enroll-done-close"
                    onClick={() => setBuyCourse(null)}
                    className="mt-7 inline-flex items-center gap-2 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#16395C] transition-colors"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#4F6D8A]">Course Enrollment</p>
                  <h3 className="mt-3 font-display font-bold text-2xl text-[#111827] leading-tight">{buyCourse.name}</h3>
                  <p className="mt-1.5 font-display font-black text-3xl text-[#0F2A43]" data-testid="enroll-price">
                    {buyCourse.price > 0 ? `₹${Number(buyCourse.price).toLocaleString("en-IN")}` : "Free"}
                  </p>
                  <div className="mt-6 space-y-3">
                    <input
                      value={buyer.name}
                      onChange={(e) => setBuyer((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Full Name"
                      data-testid="enroll-name"
                      className="w-full bg-[#F8F6F2] border border-[#E5E7EB] focus:border-[#4F6D8A] outline-none rounded-2xl px-5 py-3.5 text-sm text-[#111827] placeholder:text-[#9CA3AF] transition-colors"
                    />
                    <input
                      type="email"
                      value={buyer.email}
                      readOnly
                      placeholder="Email — course details isi par jayenge"
                      data-testid="enroll-email"
                      className="w-full bg-[#F8F6F2] border border-[#E5E7EB] rounded-2xl px-5 py-3.5 text-sm text-[#111827] placeholder:text-[#9CA3AF] opacity-70 cursor-not-allowed"
                    />
                    <input
                      value={buyer.phone}
                      onChange={(e) => setBuyer((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="Phone (optional)"
                      data-testid="enroll-phone"
                      className="w-full bg-[#F8F6F2] border border-[#E5E7EB] focus:border-[#4F6D8A] outline-none rounded-2xl px-5 py-3.5 text-sm text-[#111827] placeholder:text-[#9CA3AF] transition-colors"
                    />
                  </div>
                  <button
                    onClick={enrollPay}
                    disabled={paying}
                    data-testid="enroll-pay"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2.5 bg-[#0F2A43] text-white font-mono text-[10px] font-bold tracking-[0.22em] uppercase px-8 py-4 rounded-full hover:bg-[#16395C] transition-all duration-300 disabled:opacity-60"
                  >
                    {paying ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />}
                    {paying ? "Processing…" : buyCourse.price > 0 ? `Pay ₹${Number(buyCourse.price).toLocaleString("en-IN")} Securely` : "Enroll Free"}
                  </button>
                  {buyCourse.price > 0 && (
                    <p className="mt-4 flex items-center justify-center gap-2 font-mono text-[8px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                      <ShieldCheck size={11} className="text-[#4F6D8A]" /> Razorpay Secure · UPI · Cards · NetBanking
                    </p>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AeroFooter />
      <WhatsAppFloat />
    </motion.main>
  );
}
